import {
  BulkMessageSenderInput,
  BulkMessageSenderTypeEnum,
  UnitEnum,
  UserRoleEnum,
} from "@/__generated__/graphql";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  Skeleton,
} from "@/components/ui";
import React from "react";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InputWrapper } from "@/components/FormInputs/InputWrapper";
import { useUsersByRoles } from "@/hooks/useUsersByRoles";

export function SendFromInput() {
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.composeMessage.inputs.sendFrom",
  });

  const {
    field,
    fieldState: { error },
  } = useController({
    name: "sender",
    rules: {
      validate: {
        required: (value) => {
          if (value && Object.keys(value).length) {
            return true;
          }
          return t("error");
        },
      },
    },
  });
  const { loading, data } = useUsersByRoles({
    roles: [UserRoleEnum.Counselor, UserRoleEnum.Influencer],
  });

  const senderValue = (sendFrom: string) => {
    if (sendFrom === BulkMessageSenderTypeEnum.AssignedCounselor) {
      return { assignedCounselor: UnitEnum.Unit };
    } else {
      return { userId: sendFrom };
    }
  };

  const defaultValue = (sender?: BulkMessageSenderInput) => {
    if (sender?.assignedCounselor) {
      return BulkMessageSenderTypeEnum.AssignedCounselor;
    } else if (sender?.userId) {
      return sender.userId;
    } else {
      return;
    }
  };

  const onChange = (value: string) => field.onChange(senderValue(value));

  return (
    <div>
      {loading ? (
        <InputWrapper label={t("name")} name={field.name} error={error}>
          <Skeleton
            className="h-9 w-full px-3 py-2"
            data-testid="send-from-skeleton"
          />
        </InputWrapper>
      ) : (
        <InputWrapper label={t("name")} name={field.name} error={error}>
          <Select
            onValueChange={onChange}
            defaultValue={defaultValue(field.value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                data-testid="select-value"
                placeholder={t("placeholder")}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup className="border-b">
                <SelectItem value={BulkMessageSenderTypeEnum.AssignedCounselor}>
                  {t("assignedCounselor")}
                </SelectItem>
              </SelectGroup>
              {data
                ?.map(({ id, name }) => ({ id: id ?? "", name: name ?? "" }))
                ?.sort((so1, so2) => (so1.name < so2.name ? -1 : 1))
                .map(({ id, name }) => (
                  <SelectItem key={id} value={id}>
                    {name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </InputWrapper>
      )}
    </div>
  );
}
